// Port of Flutter's userScreen.dart — the "Me" tab, titled "Account Controls".
//
// A single column of rounded ButtonColor tiles, capped at 480 wide and centred.
// Two of them are gated on flags from the user record, exactly as in the Dart:
//   AllowTesting     == 1 -> "Test Device"   (open any device by Unique Number)
//   AllowTestingTuya == 1 -> "Scan Devices"
//
// The Dart's "Sync now (local DB)" tile is here too, master users only. It backs
// onto the SQLite offline cache (core/db/*) and reports row counts, which is the
// only way to tell the cache actually filled — a working cache and a working
// network look identical from the UI.

import React, { useEffect, useState } from 'react';
import {
  Box, ListItemButton, ListItemText, Avatar, useTheme, Switch, Typography,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button,
  CircularProgress,
} from '@mui/material';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import DocumentScannerSharpIcon from '@mui/icons-material/DocumentScannerSharp';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import BluetoothSearchingIcon from '@mui/icons-material/BluetoothSearching';
import ForumIcon from '@mui/icons-material/Forum';
import ScienceIcon from '@mui/icons-material/Science';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import InfoIcon from '@mui/icons-material/Info';
import SyncIcon from '@mui/icons-material/Sync';
import WifiIcon from '@mui/icons-material/Wifi';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BusinessIcon from '@mui/icons-material/Business';
import MemoryIcon from '@mui/icons-material/Memory';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import TabletMacIcon from '@mui/icons-material/TabletMac';
import RefreshIcon from '@mui/icons-material/Refresh';
import LogoutIcon from '@mui/icons-material/Logout';
import { useNavigate } from 'react-router-dom';
import { CenterTitleBar } from '../../components/MobileAppBar';
import { useApp } from '../../../../core/state/AppContext';
import { useMessage } from '../../components/MessageDialog';
import { fullSync, tableCounts, lastSyncedAt } from '../../../../core/db/syncService';
import { dbSupported } from '../../../../core/db/schema';
import {
  isManualOffline, setManual, hasNoNetwork, onOfflineChange,
} from '../../../../core/offline/offlineMode';
import { isKiosk } from '../../../../core/wall/kioskMode';
import { APP_VERSION } from '../../../../core/version';
import AppUpdateDialog from '../../components/AppUpdateDialog';
import AdminViewSheet from '../../components/AdminViewSheet';
import { hardRefresh } from '../../../../core/utils/hardRefresh';

function Tile({ leading, title, subtitle, onClick }) {
  const theme = useTheme();
  const c = theme.sli;
  return (
    <ListItemButton
      onClick={onClick}
      sx={{ bgcolor: c.button, borderRadius: '16px', p: 2, mb: 1.25, gap: 2 }}
    >
      {leading}
      <ListItemText
        primary={title}
        secondary={subtitle}
        primaryTypographyProps={{ sx: { color: c.text, fontSize: 18 } }}
        secondaryTypographyProps={{ sx: { color: `${c.text}B3`, fontSize: 13 } }}
      />
    </ListItemButton>
  );
}

// ── Why the admin tools are a LABELLED BLOCK and not just more tiles ───────
// There are ten of them once Companies, Device Types, Device by Unique Number,
// User Access and Installed Devices are here, against about nine ordinary ones.
// Dropped into the same flat column they double the length of the screen and
// bury "Offline Mode" and "App updates" — the two a customer actually opens —
// under a wall of things that reboot hardware and rewrite other people's
// access. The desktop never had this problem: its admin entries live behind a
// menu, visually separate by construction.
//
// So they go last, under a rule and a heading. A customer's Me tab is
// unchanged — the heading only renders when at least one tile under it does.
function SectionHeader({ title, subtitle, color }) {
  return (
    <Box sx={{ mt: 3, mb: 1.25, px: 0.5 }}>
      <Box sx={{ height: '1px', bgcolor: `${color}33`, mb: 1.5 }} />
      <Typography sx={{ color, fontSize: 13, fontWeight: 800, letterSpacing: 0.8 }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography sx={{ color: `${color}99`, fontSize: 11.5, mt: 0.25 }}>
          {subtitle}
        </Typography>
      )}
    </Box>
  );
}

export default function MeTab() {
  const theme = useTheme();
  const c = theme.sli;
  const navigate = useNavigate();
  const {
    user, profileUrl, isSuperAdmin, canTuyaTest, allLocations, viewAsUser, logout,
  } = useApp();
  // Never cleared — the document navigates away instead. Without it a second
  // tap lands in the gap while the caches are being cleared, which is real:
  // clearing is asynchronous. Same reasoning as the desktop header's spinner.
  const [refreshing, setRefreshing] = useState(false);

  const [testOpen, setTestOpen] = useState(false);
  const [updateOpen, setUpdateOpen] = useState(false);
  const [adminViewOpen, setAdminViewOpen] = useState(false);
  // Read on each render rather than held in state: the setting is changed on
  // the Wall Panel screen, so a copy taken here would go stale the moment the
  // user came back.
  const kioskOn = isKiosk();
  const [uno, setUno] = useState('');
  const [syncing, setSyncing] = useState(false);
  const { show } = useMessage();

  const [manualOffline, setManualOffline] = useState(() => isManualOffline());
  const [noNetwork, setNoNetwork] = useState(() => hasNoNetwork());
  // Connectivity can change while this tab is open, so the subtitle follows it.
  useEffect(() => onOfflineChange(() => {
    setManualOffline(isManualOffline());
    setNoNetwork(hasNoNetwork());
  }), []);

  const runSync = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const ok = await fullSync(Number(user?.UserId));
      const counts = await tableCounts();
      const at = await lastSyncedAt();
      const lines = Object.entries(counts).map(([t, n]) => `${t}: ${n}`).join('\n');
      show(
        ok ? 'Sync Complete' : 'Sync Finished (with issues)',
        `${lines}\n\nLast sync: ${at || '-'}`,
      );
    } finally {
      setSyncing(false);
    }
  };

  const masterUser = Number(user?.AllowTesting) === 1;
  const tuyaUser = Number(user?.AllowTestingTuya) === 1;

  const iconAvatar = (icon) => (
    <Avatar sx={{ bgcolor: c.back, color: c.text, width: 40, height: 40 }}>{icon}</Avatar>
  );

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: c.back }}>
      <CenterTitleBar title="Account Controls" />

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 1.875 }}>
        <Box sx={{ maxWidth: 480, mx: 'auto' }}>
          <Tile
            leading={<Avatar src={profileUrl || undefined} sx={{ width: 40, height: 40 }} />}
            title="User Profile"
            onClick={() => navigate('/profile')}
          />

          {/* Opens the PANEL — the buttons — not the editor. Settings is a gear
              on the panel, and so is the switch that makes this screen open on
              it. An empty panel sends you straight to settings. */}
          <Tile
            leading={iconAvatar(<DashboardIcon />)}
            title="Wall Panel"
            subtitle={kioskOn ? 'On — this screen opens on the panel' : 'Your shortcut buttons for this location'}
            onClick={() => navigate('/wall')}
          />

          {masterUser && (
            <Tile
              leading={iconAvatar(<AdminPanelSettingsIcon />)}
              title="Test Device"
              onClick={() => { setUno(''); setTestOpen(true); }}
            />
          )}

          {/* Flutter's "Sync now (local DB)" — forces a full pull and reports
              row counts. Without it there is no way to tell whether the offline
              cache actually filled, since a working cache looks exactly like a
              working network. Master users only. */}
          {masterUser && dbSupported() && (
            <Tile
              leading={iconAvatar(<SyncIcon />)}
              title="Sync now (local DB)"
              subtitle={syncing ? 'Syncing…' : 'Refresh the offline cache'}
              onClick={runSync}
            />
          )}

          {tuyaUser && (
            <Tile
              leading={iconAvatar(<DocumentScannerSharpIcon />)}
              title="Scan Devices"
              onClick={() => navigate('/add-device?tuya=1')}
            />
          )}

          {/* Offline Mode — the Dart's manual toggle. On, the app serves
              everything from the local cache and sends commands over Bluetooth
              instead of the internet. Useful on a bad connection, and for
              cutting data use. It also turns itself on when there is genuinely
              no network, which is why the subtitle reports the effective state
              rather than just the switch. */}
          <ListItemButton
            onClick={() => { setManual(!manualOffline); setManualOffline(!manualOffline); }}
            sx={{ bgcolor: c.button, borderRadius: '16px', p: 2, mb: 1.25, gap: 2 }}
          >
            {iconAvatar(<CloudOffIcon />)}
            <ListItemText
              primary="Offline Mode"
              secondary={
                // eslint-disable-next-line no-nested-ternary
                manualOffline
                  ? 'On — using local data and Bluetooth'
                  : (noNetwork ? 'Auto — no network detected' : 'Off')
              }
              primaryTypographyProps={{ sx: { color: c.text, fontSize: 18 } }}
              secondaryTypographyProps={{ sx: { color: `${c.text}B3`, fontSize: 13 } }}
            />
            <Switch
              checked={manualOffline}
              onChange={(e) => { setManual(e.target.checked); setManualOffline(e.target.checked); }}
              onClick={(e) => e.stopPropagation()}
            />
          </ListItemButton>

          {/* Onboarding a new device is easiest to find here, not buried in a
              room FAB — it is the first thing done with hardware out of the box. */}
          <Tile
            leading={iconAvatar(<WifiIcon />)}
            title="Set Up New Device"
            subtitle="Find a new device and give it your Wi-Fi"
            onClick={() => navigate('/wifi-setup')}
          />

          <Tile
            leading={iconAvatar(<QrCodeScannerIcon />)}
            title="Add Shared Device"
            subtitle="Scan a device's share QR to add it"
            onClick={() => navigate('/scan-share')}
          />

          {/* Every user, not just admins: this is the only surface that can
              tell "you are up to date" apart from "the update server is
              broken", and the two were indistinguishable for three weeks. */}
          <Tile
            leading={iconAvatar(<SystemUpdateAltIcon />)}
            title="App updates"
            subtitle={`Version ${APP_VERSION || '—'} — check for a newer one`}
            onClick={() => setUpdateOpen(true)}
          />

          {/* Kiosk mode. ⚠️ THE SCREEN THAT SETS IT LIVED ONLY ON DESKTOP, and
              a wall tablet runs THIS shell — so the one device the setting
              exists for could not reach it. KioskSetupScreen's own comment has
              said "on mobile it opens from a Me-tab tile" since it was written;
              this is that tile. */}
          <Tile
            leading={iconAvatar(<TabletMacIcon />)}
            title="Wall display mode"
            subtitle={kioskOn ? 'On — this device opens on its panel' : 'Make this device open on its wall panel'}
            onClick={() => navigate('/wall/kiosk')}
          />

          {/* Reloads past the caches — a plain reload is not enough; see
              core/utils/hardRefresh. Desktop has had this in its header. */}
          <Tile
            leading={iconAvatar(refreshing ? <CircularProgress size={20} sx={{ color: c.text }} /> : <RefreshIcon />)}
            title="Hard refresh"
            subtitle={refreshing ? 'Reloading…' : 'Reload the app and clear cached files'}
            onClick={() => {
              if (refreshing) return;
              setRefreshing(true);
              hardRefresh().catch(() => setRefreshing(false));
            }}
          />

          <Tile
            leading={iconAvatar(<InfoIcon />)}
            title="About Us"
            onClick={() => navigate('/about')}
          />

          {/* ⚠️ THE ONLY WAY OFF THIS ACCOUNT ON A PHONE WAS /profile — the
              desktop-styled screen reached by the top tile, whose own footer
              still tells mobile users that adding devices happens "in the Smart
              Life mobile app". Logout belongs where the account is. */}
          <Tile
            leading={iconAvatar(<LogoutIcon />)}
            title="Logout"
            subtitle={user?.MobileNumber ? `Signed in as ${user.MobileNumber}` : undefined}
            onClick={() => { logout(); navigate('/login', { replace: true }); }}
          />

          {/* ── Administration ───────────────────────────────────────────────
              Everything below writes something a customer must never be able to
              write: ownership, device types, other people's access, firmware on
              live hardware. Every tile is permission-gated, every ROUTE is
              gated again in MobileRoutes, and every screen checks a third time
              on its way in — see the note in MobileRoutes. The heading renders
              only when something under it does, so an ordinary account's Me tab
              ends at Logout above. */}
          {(isSuperAdmin || canTuyaTest) && (
            <SectionHeader
              title="ADMINISTRATION"
              subtitle="Writes live data — ownership, device types, access and firmware"
              color={c.text}
            />
          )}

          {/* First, because it governs everything under it: the other tiles act
              on whatever this one has the app pointed at. */}
          {isSuperAdmin && (
            <Tile
              leading={iconAvatar(<VisibilityIcon />)}
              title="Admin view"
              subtitle={
                // eslint-disable-next-line no-nested-ternary
                allLocations
                  ? 'All locations — every site on the server'
                  : (viewAsUser
                    ? `Viewing as ${[viewAsUser.Surname, viewAsUser.Name].filter(Boolean).join(' ')}`
                    : 'All locations, or view the app as another user')
              }
              onClick={() => setAdminViewOpen(true)}
            />
          )}

          {isSuperAdmin && (
            <Tile
              leading={iconAvatar(<MemoryIcon />)}
              title="Device by Unique Number"
              subtitle="Look a device up and assign it to a user, location and room"
              onClick={() => navigate('/device-admin')}
            />
          )}

          {isSuperAdmin && (
            <Tile
              leading={iconAvatar(<PersonAddAlt1Icon />)}
              title="User Access"
              subtitle="One user — every location, room and device they can reach"
              onClick={() => navigate('/user-access')}
            />
          )}

          {/* Next to User Access deliberately — they are the two "who/what is
              where" reports, and the pair is where the difference gets noticed:
              this one counts the PREMISES, that one counts one user's access. */}
          {isSuperAdmin && (
            <Tile
              leading={iconAvatar(<FormatListNumberedIcon />)}
              title="Installed Devices"
              subtitle="What is installed on a customer's premises"
              onClick={() => navigate('/installed')}
            />
          )}

          {isSuperAdmin && (
            <Tile
              leading={iconAvatar(<MemoryIcon />)}
              title="Device Types"
              subtitle="The DeviceTypes table — channels, firmware and UART pins"
              onClick={() => navigate('/device-types')}
            />
          )}

          {isSuperAdmin && (
            <Tile
              leading={iconAvatar(<BusinessIcon />)}
              title="Companies"
              subtitle="Company info and branding"
              onClick={() => navigate('/companies')}
            />
          )}

          {/* Bring-up tool, not a user feature: it shows the raw MQTT traffic
              and is only meaningful to someone debugging firmware. */}
          {isSuperAdmin && (
            <Tile
              leading={iconAvatar(<ForumIcon />)}
              title="Message Box"
              subtitle="Live MQTT traffic, and send commands"
              onClick={() => navigate('/mqtt')}
            />
          )}

          {isSuperAdmin && (
            <Tile
              leading={iconAvatar(<BluetoothSearchingIcon />)}
              title="BLE Test"
              subtitle="Scan, authenticate and watch the raw exchange"
              onClick={() => navigate('/ble-test')}
            />
          )}

          {/* The production bench: the last devices seen on the broker, no
              account or mapping needed. Not a customer feature — it can rewrite
              a device's type and reboot it.
              Gated on `canTuyaTest`, NOT on isSuperAdmin: the permission the
              backend actually records is the Users flag AllowTestingTuya, which
              is how the bench operators are granted it and how Flutter gated the
              same page. */}
          {canTuyaTest && (
            <Tile
              leading={iconAvatar(<ScienceIcon />)}
              title="Tuya Testing"
              subtitle="Recently seen devices — control, set type, add Wi-Fi"
              onClick={() => navigate('/tuya-testing')}
            />
          )}

          {/* Estate-wide, and the load is expensive — so it is a place you go
              deliberately, not something the app does on its own. */}
          {isSuperAdmin && (
            <Tile
              leading={iconAvatar(<SystemUpdateAltIcon />)}
              title="Firmware"
              subtitle="Every device — which need updating, and update them"
              onClick={() => navigate('/firmware')}
            />
          )}
        </Box>
      </Box>

      {/* userScreenFunctions.dart promptForUniqueNumber() */}

      <Dialog open={testOpen} onClose={() => setTestOpen(false)}
        PaperProps={{ sx: { bgcolor: c.back, borderRadius: '20px', minWidth: 300 } }}>
        <DialogTitle sx={{ color: c.text, fontWeight: 700, textAlign: 'center' }}>
          Enter Unique Number
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus fullWidth placeholder="Type here..."
            value={uno} onChange={(e) => setUno(e.target.value)}
            sx={{ mt: 1, '& .MuiInputBase-input': { color: c.text } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestOpen(false)} sx={{ color: c.text }}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!uno.trim()}
            onClick={() => { setTestOpen(false); navigate(`/device/${uno.trim()}`); }}
            sx={{ bgcolor: c.button, color: c.text }}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      <AppUpdateDialog open={updateOpen} onClose={() => setUpdateOpen(false)} />

      <AdminViewSheet open={adminViewOpen} onClose={() => setAdminViewOpen(false)} />
    </Box>
  );
}
